import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

// Helper function for the useGetTabIndex hook. It gets the index of the tab from the URL query param tab string.
const getTabIndex = (tabs, tab) => {
  if (!tab) return 0;
  const tabIndex = tabs.findIndex((t) => t.key === tab);
  return tabIndex === -1 ? 0 : tabIndex;
};

// Hook to get the index of the tab from the URL query param tab string.
// Returns the current tab index and a function to set the search params.
// This should be used whenever HorizontalTabs component needs to be based on a URL query param.
// `param` (optional): the address setting to read, "tab" unless a page shown
// inside another page needs its own (the host page already owns "tab").
const useGetTabIndex = (tabs, param = "tab") => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get(param);
  const currentTabIndex = getTabIndex(tabs, tabParam);
  return { currentTabIndex, setSearchParams };
};

// `featuredIndex` (optional): one tab drawn as a gold pill with a sparkle, to
// draw the eye (Sam, 2026-09-26: the Scrapbook inside Family Connect). Other
// tab bars are unchanged.
function HorizontalTabs({ tabs, onChange, initialIndex = 0, className = "", featuredIndex = -1 }) {
  const [activeTabIndex, setActiveTabIndex] = useState(initialIndex);

  useEffect(() => {
    setActiveTabIndex(initialIndex);
  }, [initialIndex]);

  const handleTabClick = (index) => {
    setActiveTabIndex(index);
    onChange(index);
  };

  // Enter or Space picks a tab from the keyboard. preventDefault stops Space
  // from also scrolling the page.
  const handleTabKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleTabClick(index);
    }
  };

  // flex-wrap: on a phone a row of tabs wider than the screen wraps onto a
  // second line instead of pushing the whole page sideways. Each tab keeps its
  // own words together.
  return (
    <div role="tablist" className={`flex flex-row flex-wrap gap-x-[15px] gap-y-[8px] ${featuredIndex >= 0 ? "items-center" : ""} ${className}`}>
      {tabs.map((tab, index) =>
        index === featuredIndex ? (
          <div
            key={index}
            data-testid={`horizontal-tab-${tab}`}
            role="tab"
            aria-selected={activeTabIndex === index}
            tabIndex={0}
            onKeyDown={(e) => handleTabKeyDown(e, index)}
            className={`cursor-pointer select-none whitespace-nowrap rounded-full px-[14px] py-[5px] text-base font-medium
              border border-dupontGold/70 shadow-sm transition duration-200
              hover:shadow-md motion-safe:hover:-translate-y-[1px]
              focus:outline-none focus-visible:ring-2 focus-visible:ring-dupontGold ${
                activeTabIndex === index
                  ? "bg-dupontGold text-white"
                  : "bg-gradient-to-r from-dupontGold/25 via-dupontGold/10 to-dupontGold/25 text-dupontBlue dark:text-darkText"
              }`}
            onClick={() => handleTabClick(index)}
          >
            <span aria-hidden="true" className={`mr-[6px] motion-safe:animate-pulse ${activeTabIndex === index ? "text-white" : "text-dupontGold"}`}>✦</span>
            {tab}
          </div>
        ) : (
        <div
          key={index}
          data-testid={`horizontal-tab-${tab}`}
          role="tab"
          aria-selected={activeTabIndex === index}
          tabIndex={0}
          onKeyDown={(e) => handleTabKeyDown(e, index)}
          className={`px-[5px] py-[5px] cursor-pointer whitespace-nowrap font-normal text-base dark:text-darkText ${
            activeTabIndex === index &&
            "font-medium border-b-2 border-dupontGold"
          }`}
          onClick={() => handleTabClick(index)}
        >
          {tab}
        </div>
        )
      )}
    </div>
  );
}

const SharedHorizontalTabs = {
  Component: HorizontalTabs,
  useGetTabIndex,
};

export default SharedHorizontalTabs;
